import { useState } from 'react';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import Button from '../../shared/UI/Button';
import QuestionBuilder from '../rubrics/QuestionBuilder';

export default function SectionBuilder({ sections, onChange }) {
  const [expanded, setExpanded] = useState({});

  function toggle(sid) {
    setExpanded(prev => ({ ...prev, [sid]: !prev[sid] }));
  }

  function makeSectionId() {
    const ts = Date.now().toString(36);
    const rand = Math.random().toString(36).substring(2, 6);
    return 's_' + ts + '_' + rand;
  }

  function addSection() {
    const order = sections.length + 1;
    const sid = makeSectionId();
    onChange([...sections, {
      section_id: sid,
      order,
      title: '',
      description: '',
      is_fixed: false,
      is_nda_section: false,
      visibility: 'shared',
      questions: [],
    }]);
    setExpanded(prev => ({ ...prev, [sid]: true }));
  }

  function removeSection(idx) {
    onChange(sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  }

  function setSection(idx, field, value) {
    onChange(sections.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  return (
    <div>
      {sections.map((section, idx) => (
        <div key={section.section_id} className={`section-card ${section.is_fixed ? 'fixed' : ''}`}>
          <div className="section-card-header" onClick={() => toggle(section.section_id)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                {section.order}.
              </span>
              <strong style={{ fontSize: 14 }}>
                {section.title || <em style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>Untitled Section</em>}
              </strong>
              {section.is_fixed && (
                <span className="badge badge-accent" style={{ fontSize: 10 }}>Fixed</span>
              )}
              {section.is_nda_section && (
                <span className="badge badge-warning" style={{ fontSize: 10 }}>NDA</span>
              )}
              <span className="badge badge-default" style={{ fontSize: 10 }}>{section.visibility}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {(section.questions || []).length} question{(section.questions || []).length !== 1 ? 's' : ''}
              </span>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {expanded[section.section_id] ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {expanded[section.section_id] && (
            <div className="section-card-body">
              {section.is_fixed ? (
                <div className="alert alert-info" style={{ fontSize: 13, marginBottom: 12 }}>
                  This is a fixed base section. The title, visibility and NDA flag cannot be changed.
                  You can add or edit questions.
                </div>
              ) : (
                <div className="form-grid mb-3">
                  <FormField label="Section Title" required>
                    <input className="form-input" value={section.title}
                      onChange={e => setSection(idx, 'title', e.target.value)} />
                  </FormField>
                  <SelectField label="Visibility" value={section.visibility}
                    onChange={e => setSection(idx, 'visibility', e.target.value)}
                    options={[
                      { value: 'shared', label: 'Shared (company + internal)' },
                      { value: 'internal', label: 'Internal only (never shown to company)' },
                    ]} />
                  <FormField label="Description" className="form-full">
                    <textarea className="form-textarea" rows={2} value={section.description || ''}
                      onChange={e => setSection(idx, 'description', e.target.value)}
                      placeholder="Instructions shown at the top of this section…" />
                  </FormField>
                </div>
              )}

              <p className="form-label mb-2" style={{ marginTop: 8 }}>Questions</p>
              <QuestionBuilder
                questions={section.questions || []}
                onChange={qs => setSection(idx, 'questions', qs)}
                showScores={false}
              />

              {!section.is_fixed && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
                  <Button size="sm" variant="danger" onClick={() => removeSection(idx)}>Remove Section</Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <Button variant="secondary" onClick={addSection} className="mt-3">+ Add Section</Button>
    </div>
  );
}
